// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — shot templates, for a video on deadline (plan item J10,
 * redone as stops by the Studio motion plan, 10 October 2026): stops built
 * around what the globe shows, so a journalist picks the place and the date,
 * then a template, and has a shot to adjust.
 *
 * - **turn**: the globe turns past the place (50° either side), date held;
 * - **zoomIn**: from the whole globe down onto the place, then a slow push;
 * - **timeLapse**: the camera holds on the place while the data runs from the
 *   dataset's first frame to its last (a time run);
 * - **beforeAfter**: the first date with a "Before" card, then the last with
 *   an "After" card, cross-fading between them;
 * - **pinJourney**: one stop per pin on the globe, flying from pin to pin, each
 *   in a spotlight with a callout reading the value there.
 *
 * The time-based ones need the dataset's dates, the journey needs two pins;
 * without them they give null. Steps come back without ids: the panel adds
 * them to the Khipu.
 */

import type { KhipuStep, KhipuView } from '../khipu'

export type ShotTemplate = 'turn' | 'zoomIn' | 'timeLapse' | 'beforeAfter' | 'pinJourney'
export const SHOT_TEMPLATES: readonly ShotTemplate[] = ['turn', 'zoomIn', 'timeLapse', 'beforeAfter', 'pinJourney']

/** A step without its id and number (the panel numbers it); the named fields only. */
export type TemplateStop = Partial<Pick<KhipuStep, 'title' | 'dataset' | 'view' | 'time' | 'layers' | 'pins' | 'autorotate' | 'region' | 'display' | 'prompts' | 'hold' | 'transition' | 'effects'>>

const wrapLon = (lon: number): number => ((((lon + 180) % 360) + 360) % 360) - 180

/**
 * The stops of a template. `base` is a step taken from the globe (its
 * dataset, layers, camera and date); `words` the card texts in the viewer's
 * language; `pins` the pins on the globe with their notes.
 */
export function templateStops(
  template: ShotTemplate,
  base: TemplateStop & { view: KhipuView },
  range: { startMs: number; endMs: number } | null,
  words: { before: string; after: string; lang: string },
  pins: Array<{ lat: number; lon: number; note?: string }> = [],
): TemplateStop[] | null {
  const iso = (ms: number) => new Date(ms).toISOString()
  const v = base.view
  const hasRange = !!range && range.endMs > range.startMs
  const text = (s: string) => ({ [words.lang]: s, ...(words.lang === 'en' ? {} : { en: s }) })
  switch (template) {
    case 'turn':
      return [
        { ...base, view: { ...v, lon: wrapLon(v.lon - 50) }, hold: 0.5 },
        { ...base, view: { ...v, lon: wrapLon(v.lon + 50) }, hold: 0.5, transition: { move: 'glide', seconds: 8, feel: 'linear' } },
      ]
    case 'zoomIn':
      return [
        { ...base, view: { ...v, zoom: 1.4, pitch: 0 }, hold: 1 },
        {
          ...base, view: { ...v, zoom: Math.min(9.5, Math.max(v.zoom, 4)) }, hold: 4,
          transition: { move: 'glide', seconds: 4, feel: 'gentle', during: 'pushIn', amount: 0.4 },
        },
      ]
    case 'timeLapse':
      if (!hasRange) return null
      // The axis end is exclusive: the last frame starts just before it.
      return [
        { ...base, time: { at: iso(range!.startMs) }, hold: 1 },
        { ...base, time: { at: iso(range!.endMs - 1) }, hold: 2, transition: { move: 'cut', seconds: 10, data: 'timeRun' } },
      ]
    case 'beforeAfter':
      if (!hasRange) return null
      return [
        { ...base, time: { at: iso(range!.startMs) }, hold: 3.5, effects: { titleCard: { text: text(words.before), seconds: 1.5 } } },
        {
          ...base, time: { at: iso(range!.endMs - 1) }, hold: 3.5,
          transition: { move: 'cut', seconds: 1.5, data: 'crossfade' },
          effects: { titleCard: { text: text(words.after), seconds: 1.5 } },
        },
      ]
    case 'pinJourney':
      if (pins.length < 2) return null
      return pins.map((p, i) => ({
        ...base,
        view: { lat: p.lat, lon: p.lon, zoom: Math.max(v.zoom, 3.5), bearing: 0, pitch: 0 },
        hold: 4,
        ...(i ? { transition: { move: 'fly' as const, feel: 'gentle' as const, during: 'orbit' as const, amount: 20 } } : { transition: { during: 'orbit' as const, amount: 20 } }),
        effects: {
          spotlight: { lat: p.lat, lon: p.lon, radius: 6, dim: 0.5, outline: true },
          callouts: [{ lat: p.lat, lon: p.lon, label: p.note?.trim() || `${i + 1}`, value: true }],
        },
      }))
  }
}
